/**
 * Disconnect Confirmation Dialog
 * 
 * Clear confirmation dialog explaining what happens when disconnecting a bank.
 */

import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  AlertTriangle,
  Unlink,
  Clock,
  Database,
  CheckCircle2
} from 'lucide-react';

export default function DisconnectConfirmationDialog({ 
  open, 
  onOpenChange, 
  onConfirm, 
  connectionName = 'this bank',
  loading = false 
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="disconnect-confirmation-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Unlink className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <DialogTitle className="text-xl">Disconnect Bank</DialogTitle>
              <DialogDescription className="text-sm">
                Remove {connectionName} from ARA Wealth
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* What happens when you disconnect */}
        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            When you disconnect this bank connection:
          </p>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-foreground">Syncing will stop immediately</p>
                <p className="text-xs text-muted-foreground">
                  We will no longer retrieve new transactions or balances from this account.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-foreground">Access token will be revoked</p>
                <p className="text-xs text-muted-foreground">
                  The secure connection between ARA Wealth and your bank will be terminated.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Database className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-foreground">Historical data handling</p>
                <p className="text-xs text-muted-foreground">
                  Historical transaction data that has already been imported may remain available to you for financial history continuity. Financial data is retained only as long as reasonably required to provide the service and in accordance with the New Zealand Privacy Act.
                </p>
              </div>
            </div>
          </div>

          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                You can reconnect anytime by starting the bank connection flow again.
              </span>
            </p>
          </div>
        </div>

        {/* Actions */}
        <DialogFooter className="flex gap-3 sm:gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={loading}
            className="flex-1"
            data-testid="confirm-disconnect-btn"
          >
            {loading ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
